import { Box, CircularProgress } from '@pulse/ui';
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import { OrganizationGateDialog } from '../features/auth/components/OrganizationGateDialog';

// Authentication-only — checks isAuthenticated and nothing else beyond the BU/Management gate
// below. Any logged-in user, regardless of roles, passes. A permission-checking guard is a
// future concern for when domain modules with real authorization needs are built.
export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // Only reachable once isLoading is false and isAuthenticated is true, so `user` is guaranteed
  // to be the freshly-loaded /auth/me object here — never undefined, never mid-fetch. This is
  // what prevents the gate from flashing or evaluating against stale/absent data; don't hoist
  // this check above the isLoading/isAuthenticated checks above.
  const needsOrganizationGate = user && !user.businessUnitId && !user.isManagement;

  return (
    <>
      {needsOrganizationGate && <OrganizationGateDialog />}
      {children}
    </>
  );
}
